export type Language = 'en' | 'ru' | 'he' | 'ar';

export const LANGUAGES: Array<{ code: Language; label: string; locale: string; dir: 'ltr' | 'rtl' }> = [
  { code: 'en', label: 'English', locale: 'en-US', dir: 'ltr' },
  { code: 'ru', label: 'Русский', locale: 'ru-RU', dir: 'ltr' },
  { code: 'he', label: 'עברית', locale: 'he-IL', dir: 'rtl' },
  { code: 'ar', label: 'العربية', locale: 'ar', dir: 'rtl' }
];

export type TranslationKey =
  | 'admin'
  | 'adminActionFailed'
  | 'adminRequestFailed'
  | 'ageConfirm'
  | 'analytics'
  | 'arrowHidden'
  | 'balance'
  | 'bank'
  | 'block'
  | 'browser'
  | 'checking'
  | 'cityHit'
  | 'close'
  | 'completeOnboarding'
  | 'country'
  | 'countryHit'
  | 'createRound'
  | 'directionApprox'
  | 'displayNameRequired'
  | 'displayNameTaken'
  | 'device'
  | 'eligibilityFailed'
  | 'enterRound'
  | 'explorerName'
  | 'feed'
  | 'finalizeRound'
  | 'foundAt'
  | 'foundPlace'
  | 'game'
  | 'gamesPlayed'
  | 'generatePois'
  | 'geoBlocked'
  | 'insufficientPoints'
  | 'incompleteEntries'
  | 'invalidMapPoint'
  | 'invalidDisplayName'
  | 'ipAddress'
  | 'language'
  | 'leaderboard'
  | 'lastSeen'
  | 'load'
  | 'loadFailed'
  | 'loadingHotCold'
  | 'loadingRound'
  | 'mapLayer'
  | 'nameFailed'
  | 'nameSaved'
  | 'nearestTreasure'
  | 'next'
  | 'nextBankPreview'
  | 'noClaimsYet'
  | 'noEventsYet'
  | 'noName'
  | 'noTreasureCards'
  | 'notFound'
  | 'notAvailable'
  | 'onboardingRequired'
  | 'operations'
  | 'other'
  | 'participants'
  | 'playerBlocked'
  | 'playerUnblocked'
  | 'players'
  | 'playersListHint'
  | 'placeInfo'
  | 'photoUnavailable'
  | 'pointsAfterRound'
  | 'pointsCredited'
  | 'pointsGranted'
  | 'poiPoolGenerated'
  | 'pois'
  | 'publicGameName'
  | 'publicCardsAfterRound'
  | 'refresh'
  | 'refreshFailed'
  | 'profile'
  | 'revealedTreasures'
  | 'round'
  | 'roundCreated'
  | 'roundFinalized'
  | 'roundNotActive'
  | 'rules'
  | 'save'
  | 'sameIpPlayers'
  | 'satelliteLayer'
  | 'screen'
  | 'settingUpdated'
  | 'sessions'
  | 'source'
  | 'spent'
  | 'tap'
  | 'tapCooldown'
  | 'tapFailed'
  | 'taps'
  | 'timer'
  | 'timezone'
  | 'topUpAmount'
  | 'topUpBalance'
  | 'topUpCancel'
  | 'topUpFailed'
  | 'topUpHint'
  | 'topUpSubmit'
  | 'topUpSuccess'
  | 'topUpTitle'
  | 'totalWon'
  | 'treasureCards'
  | 'treasureFound'
  | 'treasureHuntDemo'
  | 'treasuresFoundCount'
  | 'treasuresLeft'
  | 'unblock'
  | 'visibleNameWarning'
  | 'waiting';

const translations: Record<Language, Record<TranslationKey, string>> = {
  en: {
    admin: 'Admin',
    adminActionFailed: 'Admin action failed',
    adminRequestFailed: 'Admin request failed',
    ageConfirm: 'I confirm that I am 18 or older.',
    analytics: 'Analytics',
    arrowHidden: 'Arrow hidden under 100 m',
    balance: 'Balance',
    bank: 'Bank',
    block: 'Block',
    browser: 'Browser',
    checking: 'Checking',
    cityHit: 'Excellent. You found the right city',
    close: 'Close',
    completeOnboarding: 'Complete onboarding first',
    country: 'Country',
    countryHit: 'Great. You found the right country',
    createRound: 'Create Round',
    directionApprox: 'Direction approximate',
    device: 'Device',
    displayNameRequired: 'Set public game name first.',
    displayNameTaken: 'This public game name is already taken.',
    eligibilityFailed: 'Eligibility failed',
    enterRound: 'Enter Round',
    explorerName: 'Explorer name',
    feed: 'Feed',
    finalizeRound: 'Finalize Round',
    foundAt: 'Found at',
    foundPlace: 'Found place',
    game: 'Game',
    gamesPlayed: 'Games',
    generatePois: 'Generate POIs',
    geoBlocked: 'This country is excluded for the Web MVP0 demo.',
    insufficientPoints: 'Not enough points.',
    incompleteEntries: 'Incomplete entries',
    invalidMapPoint: 'Choose a point inside the visible world map.',
    invalidDisplayName: 'Use a valid public game name.',
    ipAddress: 'IP address',
    language: 'Language',
    leaderboard: 'Leaderboard',
    lastSeen: 'Last seen',
    load: 'Load',
    loadFailed: 'Load failed',
    loadingHotCold: 'Loading HotCold',
    loadingRound: 'Loading round',
    mapLayer: 'Map',
    nameFailed: 'Name failed',
    nameSaved: 'Name saved',
    nearestTreasure: 'to nearest treasure',
    next: 'Next',
    nextBankPreview: 'Next bank preview',
    noClaimsYet: 'No claims yet',
    noEventsYet: 'No events yet',
    noName: 'No name',
    noTreasureCards: 'No treasure cards yet',
    notFound: 'not found',
    notAvailable: 'N/A',
    onboardingRequired: 'Complete onboarding first.',
    operations: 'HotCold Operations',
    other: 'Other',
    participants: 'participants',
    playerBlocked: 'Player blocked',
    playerUnblocked: 'Player unblocked',
    players: 'Players',
    playersListHint: 'Only players with a public game name are shown here.',
    placeInfo: 'Place info',
    photoUnavailable: 'Photo unavailable',
    pointsAfterRound: 'points after round',
    pointsCredited: 'points credited',
    pointsGranted: 'Points granted',
    poiPoolGenerated: 'POI pool generated',
    pois: 'POIs',
    publicGameName: 'Public game name',
    publicCardsAfterRound: 'Cards from active rounds open after the round ends.',
    refresh: 'Refresh',
    refreshFailed: 'Refresh failed',
    profile: 'Profile',
    revealedTreasures: 'Revealed treasures',
    round: 'Round',
    roundCreated: 'Round created',
    roundFinalized: 'Round finalized',
    roundNotActive: 'Round is not active.',
    rules: 'Rules',
    save: 'Save',
    sameIpPlayers: 'Same IP players',
    satelliteLayer: 'Sat',
    screen: 'Screen',
    settingUpdated: 'updated',
    sessions: 'Sessions',
    source: 'Source',
    spent: 'Spent',
    tap: 'Tap',
    tapCooldown: 'Tap cooldown: 2 seconds',
    tapFailed: 'Tap failed',
    taps: 'Taps',
    timer: 'Timer',
    timezone: 'Timezone',
    topUpAmount: 'Amount',
    topUpBalance: 'Top up balance',
    topUpCancel: 'Cancel',
    topUpFailed: 'Top up failed',
    topUpHint: 'Demo mode: enter how many non-redeemable points to add. No payment is processed.',
    topUpSubmit: 'Add points',
    topUpSuccess: 'Balance topped up',
    topUpTitle: 'Top up demo balance',
    totalWon: 'Total won',
    treasureCards: 'Treasure cards',
    treasureFound: 'Treasure found',
    treasureHuntDemo: 'Treasure hunt demo',
    treasuresFoundCount: 'Treasures found',
    treasuresLeft: 'Treasures left',
    unblock: 'Unblock',
    visibleNameWarning: 'This name is visible to everyone in the round feed and leaderboard.',
    waiting: 'Waiting'
  },
  ru: {
    admin: 'Админ',
    adminActionFailed: 'Действие админа не выполнено',
    adminRequestFailed: 'Запрос админа не выполнен',
    ageConfirm: 'Я подтверждаю, что мне 18 лет или больше.',
    analytics: 'Аналитика',
    arrowHidden: 'Стрелка скрыта ближе 100 м',
    balance: 'Баланс',
    bank: 'Банк',
    block: 'Блок',
    browser: 'Браузер',
    checking: 'Проверка',
    cityHit: 'Отлично. Вы нашли нужный город',
    close: 'Закрыть',
    completeOnboarding: 'Сначала завершите вход',
    country: 'Страна',
    countryHit: 'Отлично. Вы нашли нужную страну',
    createRound: 'Создать раунд',
    directionApprox: 'Направление примерное',
    device: 'Устройство',
    displayNameRequired: 'Сначала задайте публичное игровое имя.',
    displayNameTaken: 'Это публичное игровое имя уже занято.',
    eligibilityFailed: 'Проверка доступа не пройдена',
    enterRound: 'Войти в раунд',
    explorerName: 'Имя исследователя',
    feed: 'Лента',
    finalizeRound: 'Завершить раунд',
    foundAt: 'Найдено',
    foundPlace: 'Место находки',
    game: 'Игра',
    gamesPlayed: 'Игр',
    generatePois: 'Сгенерировать POI',
    geoBlocked: 'Эта страна исключена из Web MVP0 demo.',
    insufficientPoints: 'Недостаточно очков.',
    incompleteEntries: 'Незавершенные входы',
    invalidMapPoint: 'Выберите точку внутри видимой карты мира.',
    invalidDisplayName: 'Введите корректное публичное игровое имя.',
    ipAddress: 'IP-адрес',
    language: 'Язык',
    leaderboard: 'Лидеры',
    lastSeen: 'Последняя активность',
    load: 'Загрузить',
    loadFailed: 'Загрузка не удалась',
    loadingHotCold: 'Загрузка HotCold',
    loadingRound: 'Загрузка раунда',
    mapLayer: 'Карта',
    nameFailed: 'Имя не сохранено',
    nameSaved: 'Имя сохранено',
    nearestTreasure: 'до ближайшего сокровища',
    next: 'Следующий',
    nextBankPreview: 'Банк следующего раунда',
    noClaimsYet: 'Находок пока нет',
    noEventsYet: 'Событий пока нет',
    noName: 'Без имени',
    noTreasureCards: 'Карточек сокровищ пока нет',
    notFound: 'не найдено',
    notAvailable: 'нет данных',
    onboardingRequired: 'Сначала завершите вход.',
    operations: 'Операции HotCold',
    other: 'Другое',
    participants: 'участников',
    playerBlocked: 'Игрок заблокирован',
    playerUnblocked: 'Игрок разблокирован',
    players: 'Игроки',
    playersListHint: 'Здесь показаны только игроки, которые задали публичное имя.',
    placeInfo: 'О месте',
    photoUnavailable: 'Фото недоступно',
    pointsAfterRound: 'очков после раунда',
    pointsCredited: 'очков начислено',
    pointsGranted: 'Очки начислены',
    poiPoolGenerated: 'Пул POI сгенерирован',
    pois: 'POI',
    publicGameName: 'Публичное игровое имя',
    publicCardsAfterRound: 'Карточки активного раунда открываются после завершения раунда.',
    refresh: 'Обновить',
    refreshFailed: 'Обновление не удалось',
    profile: 'Профиль',
    revealedTreasures: 'Открытые сокровища',
    round: 'Раунд',
    roundCreated: 'Раунд создан',
    roundFinalized: 'Раунд завершен',
    roundNotActive: 'Раунд не активен.',
    rules: 'Правила',
    save: 'Сохранить',
    sameIpPlayers: 'Игроков с тем же IP',
    satelliteLayer: 'Спутник',
    screen: 'Экран',
    settingUpdated: 'обновлено',
    sessions: 'Сессии',
    source: 'Источник',
    spent: 'Потрачено',
    tap: 'Тап',
    tapCooldown: 'Кулдаун тапа: 2 секунды',
    tapFailed: 'Тап не выполнен',
    taps: 'Тапы',
    timer: 'Таймер',
    timezone: 'Часовой пояс',
    topUpAmount: 'Сумма',
    topUpBalance: 'Пополнить баланс',
    topUpCancel: 'Отмена',
    topUpFailed: 'Пополнение не удалось',
    topUpHint: 'Демо-режим: введите, сколько non-redeemable очков добавить. Оплата не выполняется.',
    topUpSubmit: 'Добавить очки',
    topUpSuccess: 'Баланс пополнен',
    topUpTitle: 'Пополнить демо-баланс',
    totalWon: 'Всего выиграно',
    treasureCards: 'Карточки сокровищ',
    treasureFound: 'Сокровище найдено',
    treasureHuntDemo: 'Демо охоты за сокровищами',
    treasuresFoundCount: 'Найдено сокровищ',
    treasuresLeft: 'Осталось сокровищ',
    unblock: 'Разблок',
    visibleNameWarning: 'Это имя видно всем в ленте раунда и таблице лидеров.',
    waiting: 'Ожидание'
  },
  he: {
    admin: 'ניהול',
    adminActionFailed: 'פעולת ניהול נכשלה',
    adminRequestFailed: 'בקשת ניהול נכשלה',
    ageConfirm: 'אני מאשר/ת שאני בן/בת 18 ומעלה.',
    analytics: 'אנליטיקה',
    arrowHidden: 'החץ מוסתר מתחת ל-100 מ׳',
    balance: 'יתרה',
    bank: 'קופה',
    block: 'חסימה',
    browser: 'דפדפן',
    checking: 'בודק',
    cityHit: 'מצוין. מצאתם את העיר הנכונה',
    close: 'סגירה',
    completeOnboarding: 'השלימו תחילה את הכניסה',
    country: 'מדינה',
    countryHit: 'מצוין. מצאתם את המדינה הנכונה',
    createRound: 'יצירת סבב',
    directionApprox: 'הכיוון משוער',
    device: 'מכשיר',
    displayNameRequired: 'קודם צריך לבחור שם משחק ציבורי.',
    displayNameTaken: 'שם המשחק הציבורי הזה כבר תפוס.',
    eligibilityFailed: 'בדיקת הזכאות נכשלה',
    enterRound: 'כניסה לסבב',
    explorerName: 'שם חוקר',
    feed: 'עדכונים',
    finalizeRound: 'סיום סבב',
    foundAt: 'נמצא ב',
    foundPlace: 'מקום המציאה',
    game: 'משחק',
    gamesPlayed: 'משחקים',
    generatePois: 'יצירת נקודות עניין',
    geoBlocked: 'המדינה הזו חסומה בדמו Web MVP0.',
    insufficientPoints: 'אין מספיק נקודות.',
    incompleteEntries: 'כניסות שלא הושלמו',
    invalidMapPoint: 'בחרו נקודה בתוך מפת העולם הגלויה.',
    invalidDisplayName: 'בחרו שם משחק ציבורי תקין.',
    ipAddress: 'כתובת IP',
    language: 'שפה',
    leaderboard: 'טבלת מובילים',
    lastSeen: 'פעילות אחרונה',
    load: 'טעינה',
    loadFailed: 'הטעינה נכשלה',
    loadingHotCold: 'HotCold נטען',
    loadingRound: 'הסבב נטען',
    mapLayer: 'מפה',
    nameFailed: 'שמירת השם נכשלה',
    nameSaved: 'השם נשמר',
    nearestTreasure: 'לאוצר הקרוב ביותר',
    next: 'הבא',
    nextBankPreview: 'תצוגת קופת הסבב הבא',
    noClaimsYet: 'אין מציאות עדיין',
    noEventsYet: 'אין אירועים עדיין',
    noName: 'ללא שם',
    noTreasureCards: 'אין עדיין כרטיסי אוצר',
    notFound: 'לא נמצא',
    notAvailable: 'אין נתונים',
    onboardingRequired: 'השלימו תחילה את הכניסה.',
    operations: 'תפעול HotCold',
    other: 'אחר',
    participants: 'משתתפים',
    playerBlocked: 'השחקן נחסם',
    playerUnblocked: 'חסימת השחקן הוסרה',
    players: 'שחקנים',
    playersListHint: 'מוצגים כאן רק שחקנים שבחרו שם משחק ציבורי.',
    placeInfo: 'מידע על המקום',
    photoUnavailable: 'התמונה אינה זמינה',
    pointsAfterRound: 'נקודות אחרי הסבב',
    pointsCredited: 'נקודות זוכו',
    pointsGranted: 'נקודות הוענקו',
    poiPoolGenerated: 'מאגר נקודות העניין נוצר',
    pois: 'נקודות עניין',
    publicGameName: 'שם משחק ציבורי',
    publicCardsAfterRound: 'כרטיסים מסבבים פעילים נפתחים אחרי סיום הסבב.',
    refresh: 'רענון',
    refreshFailed: 'הרענון נכשל',
    profile: 'פרופיל',
    revealedTreasures: 'אוצרות שנחשפו',
    round: 'סבב',
    roundCreated: 'הסבב נוצר',
    roundFinalized: 'הסבב הסתיים',
    roundNotActive: 'הסבב אינו פעיל.',
    rules: 'כללים',
    save: 'שמירה',
    sameIpPlayers: 'שחקנים עם אותו IP',
    satelliteLayer: 'לוויין',
    screen: 'מסך',
    settingUpdated: 'עודכן',
    sessions: 'סשנים',
    source: 'מקור',
    spent: 'הוצא',
    tap: 'לחיצה',
    tapCooldown: 'המתנה בין לחיצות: 2 שניות',
    tapFailed: 'הלחיצה נכשלה',
    taps: 'לחיצות',
    timer: 'זמן',
    timezone: 'אזור זמן',
    topUpAmount: 'סכום',
    topUpBalance: 'טעינת יתרה',
    topUpCancel: 'ביטול',
    topUpFailed: 'טעינת היתרה נכשלה',
    topUpHint: 'מצב דמו: הזינו כמה נקודות שאינן ניתנות למימוש להוסיף. לא מתבצע תשלום.',
    topUpSubmit: 'הוספת נקודות',
    topUpSuccess: 'היתרה נטענה',
    topUpTitle: 'טעינת יתרת דמו',
    totalWon: 'סה״כ זכיות',
    treasureCards: 'כרטיסי אוצר',
    treasureFound: 'נמצא אוצר',
    treasureHuntDemo: 'דמו ציד אוצרות',
    treasuresFoundCount: 'אוצרות שנמצאו',
    treasuresLeft: 'אוצרות שנותרו',
    unblock: 'הסרת חסימה',
    visibleNameWarning: 'השם הזה גלוי לכולם בעדכוני הסבב ובטבלת המובילים.',
    waiting: 'בהמתנה'
  },
  ar: {
    admin: 'الإدارة',
    adminActionFailed: 'فشل إجراء الإدارة',
    adminRequestFailed: 'فشل طلب الإدارة',
    ageConfirm: 'أؤكد أن عمري 18 عامًا أو أكثر.',
    analytics: 'التحليلات',
    arrowHidden: 'يختفي السهم تحت 100 م',
    balance: 'الرصيد',
    bank: 'البنك',
    block: 'حظر',
    browser: 'المتصفح',
    checking: 'جار التحقق',
    cityHit: 'ممتاز. لقد وجدت المدينة الصحيحة',
    close: 'إغلاق',
    completeOnboarding: 'أكمل الدخول أولًا',
    country: 'الدولة',
    countryHit: 'رائع. لقد وجدت الدولة الصحيحة',
    createRound: 'إنشاء جولة',
    directionApprox: 'الاتجاه تقريبي',
    device: 'الجهاز',
    displayNameRequired: 'اختر اسمًا عامًا للعبة أولًا.',
    displayNameTaken: 'اسم اللعبة العام هذا مستخدم بالفعل.',
    eligibilityFailed: 'فشل التحقق من الأهلية',
    enterRound: 'دخول الجولة',
    explorerName: 'اسم المستكشف',
    feed: 'النشاط',
    finalizeRound: 'إنهاء الجولة',
    foundAt: 'تم العثور في',
    foundPlace: 'مكان الاكتشاف',
    game: 'اللعبة',
    gamesPlayed: 'الألعاب',
    generatePois: 'إنشاء نقاط الاهتمام',
    geoBlocked: 'هذه الدولة مستبعدة من نسخة Web MVP0 التجريبية.',
    insufficientPoints: 'لا توجد نقاط كافية.',
    incompleteEntries: 'دخول غير مكتمل',
    invalidMapPoint: 'اختر نقطة داخل خريطة العالم الظاهرة.',
    invalidDisplayName: 'استخدم اسم لعبة عامًا صالحًا.',
    ipAddress: 'عنوان IP',
    language: 'اللغة',
    leaderboard: 'لوحة المتصدرين',
    lastSeen: 'آخر نشاط',
    load: 'تحميل',
    loadFailed: 'فشل التحميل',
    loadingHotCold: 'جار تحميل HotCold',
    loadingRound: 'جار تحميل الجولة',
    mapLayer: 'خريطة',
    nameFailed: 'فشل حفظ الاسم',
    nameSaved: 'تم حفظ الاسم',
    nearestTreasure: 'إلى أقرب كنز',
    next: 'التالي',
    nextBankPreview: 'بنك الجولة التالية',
    noClaimsYet: 'لا توجد اكتشافات بعد',
    noEventsYet: 'لا توجد أحداث بعد',
    noName: 'بلا اسم',
    noTreasureCards: 'لا توجد بطاقات كنز بعد',
    notFound: 'لم يتم العثور عليه',
    notAvailable: 'لا توجد بيانات',
    onboardingRequired: 'أكمل الدخول أولًا.',
    operations: 'عمليات HotCold',
    other: 'أخرى',
    participants: 'مشاركون',
    playerBlocked: 'تم حظر اللاعب',
    playerUnblocked: 'تم رفع حظر اللاعب',
    players: 'اللاعبون',
    playersListHint: 'يظهر هنا فقط اللاعبون الذين اختاروا اسمًا عامًا للعبة.',
    placeInfo: 'معلومات المكان',
    photoUnavailable: 'الصورة غير متاحة',
    pointsAfterRound: 'نقاط بعد الجولة',
    pointsCredited: 'نقاط تمت إضافتها',
    pointsGranted: 'تمت إضافة النقاط',
    poiPoolGenerated: 'تم إنشاء مجموعة نقاط الاهتمام',
    pois: 'نقاط الاهتمام',
    publicGameName: 'اسم اللعبة العام',
    publicCardsAfterRound: 'تظهر بطاقات الجولات النشطة بعد انتهاء الجولة.',
    refresh: 'تحديث',
    refreshFailed: 'فشل التحديث',
    profile: 'الملف',
    revealedTreasures: 'الكنوز المكشوفة',
    round: 'الجولة',
    roundCreated: 'تم إنشاء الجولة',
    roundFinalized: 'تم إنهاء الجولة',
    roundNotActive: 'الجولة غير نشطة.',
    rules: 'القواعد',
    save: 'حفظ',
    sameIpPlayers: 'لاعبون بنفس IP',
    satelliteLayer: 'قمر',
    screen: 'الشاشة',
    settingUpdated: 'تم التحديث',
    sessions: 'الجلسات',
    source: 'المصدر',
    spent: 'تم إنفاقه',
    tap: 'نقرة',
    tapCooldown: 'مهلة النقر: ثانيتان',
    tapFailed: 'فشلت النقرة',
    taps: 'النقرات',
    timer: 'المؤقت',
    timezone: 'المنطقة الزمنية',
    topUpAmount: 'المبلغ',
    topUpBalance: 'شحن الرصيد',
    topUpCancel: 'إلغاء',
    topUpFailed: 'فشل شحن الرصيد',
    topUpHint: 'وضع تجريبي: أدخل عدد النقاط غير القابلة للاسترداد لإضافتها. لا تتم معالجة أي دفع.',
    topUpSubmit: 'إضافة النقاط',
    topUpSuccess: 'تم شحن الرصيد',
    topUpTitle: 'شحن رصيد تجريبي',
    totalWon: 'إجمالي الفوز',
    treasureCards: 'بطاقات الكنز',
    treasureFound: 'تم العثور على كنز',
    treasureHuntDemo: 'عرض صيد الكنوز',
    treasuresFoundCount: 'الكنوز المكتشفة',
    treasuresLeft: 'الكنوز المتبقية',
    unblock: 'رفع الحظر',
    visibleNameWarning: 'هذا الاسم ظاهر للجميع في نشاط الجولة ولوحة المتصدرين.',
    waiting: 'انتظار'
  }
};

export function languageMeta(language: Language) {
  return LANGUAGES.find((item) => item.code === language) ?? LANGUAGES[0]!;
}

export function readStoredLanguage(): Language {
  const stored = window.localStorage.getItem('hotcold_language');
  return LANGUAGES.some((item) => item.code === stored) ? stored as Language : 'en';
}

export function t(language: Language, key: TranslationKey): string {
  return translations[language][key] ?? translations.en[key] ?? key;
}
